import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TyreChangeSettings } from '@cbgms/api'
import { loadWorkorderActivitiesList } from 'app/settings/state/WorkorderActivities/actions'
import { loadTyreChangeSettings, updateTyreChangeSettings } from './actions'
import { useQuery } from '@tanstack/react-query'
import { BasicFormLayout, Form } from '@carsys/form'
import * as Yup from 'yup'
import { ChangeTyreFields } from './ChangeTyreFields'
import { useNotifier } from '@cbgms/base/notifier'

export function parseTyreTreshold(input: string | number): number {
  if (typeof input === 'number') return input

  const parsedValue = parseFloat(input)

  if (Number.isNaN(parsedValue)) return 0

  return parsedValue
}

export const EditContainer = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const notifier = useNotifier()

  const { data, isLoading, refetch } = useQuery(['GET_TYRE_CHANGE_SETTINGS'], async () => {
    const [settings, activities] = await Promise.all([
      dispatch(loadTyreChangeSettings()),
      dispatch(
        loadWorkorderActivitiesList({
          fields: 'Activity.UUID,Activity.Description'
        })
      )
    ])

    return { settings: settings.Data, activities: activities.Data }
  })

  const handleSubmit = async (values: TyreChangeSettings) => {
    const settings: TyreChangeSettings = {
      ...values,
      SummerMinThreshold: parseTyreTreshold(values.SummerMinThreshold),
      WinterMinThreshold: parseTyreTreshold(values.WinterMinThreshold)
    }

    dispatch(updateTyreChangeSettings(settings))
    refetch()
    return values
  }

  const onSubmitSuccess = () => {
    notifier.success(t('TyreChange.SuccessfullyUpdated'))
  }

  if (isLoading || !data || !data.activities) return null

  const lessThanMessage = t('global.field_value_max', { value: 20 })
  const greaterThanMessage = t('global.field_value_min', { value: 0.1 })

  const validationSchema = Yup.object().shape({
    SummerMinThreshold: Yup.number().min(0.1, greaterThanMessage).max(20, lessThanMessage).required(),
    WinterMinThreshold: Yup.number().min(0.1, greaterThanMessage).max(20, lessThanMessage).required()
  })

  return (
    <Form
      validationSchema={validationSchema}
      initialValues={data.settings}
      onSubmit={handleSubmit}
      onSubmitSuccess={onSubmitSuccess}
      enableReinitialize
    >
      <BasicFormLayout>
        <ChangeTyreFields activityOptions={data.activities} />
      </BasicFormLayout>
    </Form>
  )
}
